import type { AnchorCategoryUpdate, AssetService } from '@/modules/databaseService/types';
import type { RendererBridge } from '@/modules/rendererBridge';
import { sendPlacement, type Placement } from '@/modules/rendererBridge';
import { convertToAsset, createAssetInstance } from '@/utils/dataConverters';
import { SupabaseAssetInstance, SupabaseAssetType, ASSET_SOURCES, ASSET_CATEGORIES } from '@/types/assetTypes';
import { MediaClipOverrides, SupabaseClipInstance } from '@/types/clipTypes';
import { Uid } from '@/utils/utils';
import { isBackgroundAsset } from '@/pages/editor/components/timeline/TimelineCategoryItem/assetCapabilities';
import { INITIAL_TIMELINE_LENGTH_SECONDS } from '@/pages/editor/components/timeline/utils';
import { captureError } from '@/utils/sentry';
import { parseVector3String, formatVector3String } from '@/utils/numberUtils';
import { safeParseJsonObject } from '@/utils/safeParseJson';
import type { IAnalyticsService } from '@/modules/analytics';
import type { CreateKeyframeData, Keyframe } from '@/modules/keyframes/types';
import { poseStated } from '@/modules/keyframes/localEdits';
import { placedKeyframeTransform } from '@/modules/keyframes/anchorKeyframes';
import { markAnchorThumbnails } from '@/modules/uiImages/scanImages';
import { isPlaceholderThumbnailUrl } from '@/constants/placeholderThumbnails';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll, PAGE_LIMIT } from '@/infrastructure/api/v1/lists';
import { sendBatchSkippingMissing } from '@/infrastructure/api/v1/batches';
import { assetTypeToLegacy, assetTypePatchBody, type AssetTypeDto, type LegacyAssetType } from '@/infrastructure/api/v1/mappers/assetTypes';
import {
  assetInstanceToLegacy,
  assetInstanceFromLegacy,
  assetInstanceCreateBody,
  assetInstancePatchBody,
  assetInstanceBatchItem,
  type AssetInstanceDto,
  type LegacyAssetInstance,
} from '@/infrastructure/api/v1/mappers/assetInstances';
import { keyframeCreateBody, type KeyframeDto, type LegacyKeyframe } from '@/infrastructure/api/v1/mappers/keyframes';
import { KeyframeBridgeAdapter } from './KeyframeBridgeAdapter';
import { clipInstanceRow, clipInstanceBody, mediaClipTypeId, mediaClipInstanceRow } from './ClipAdapter';

import type { WriteScope } from '@/modules/undoRedo';
const assetTypeRow = (dto: AssetTypeDto) => assetTypeToLegacy(dto) as unknown as SupabaseAssetType;
const assetInstanceRow = (dto: AssetInstanceDto) => assetInstanceToLegacy(dto) as unknown as SupabaseAssetInstance;

/** The first asset type matching the filters, as the generic route's maybeSingle() picked one. */
async function firstAssetType(query: { sourceName: string; category?: string[] }): Promise<SupabaseAssetType | null> {
  const { data, error } = await toResult(v1.GET('/api/v1/asset-types', { params: { query: { ...query, limit: 1 } } }));
  if (error) throw error;
  return data.items[0] ? assetTypeRow(data.items[0]) : null;
}

/** Changes one asset instance and returns its row. */
async function patchAssetInstance(
  assetInstanceId: string,
  changes: Partial<SupabaseAssetInstance> | Partial<LegacyAssetInstance>,
  scope?: WriteScope
): Promise<SupabaseAssetInstance> {
  const { data, error } = await toResult(v1.PATCH('/api/v1/asset-instances/{uniqueId}', {
    params: { path: { uniqueId: assetInstanceId } },
    body: assetInstancePatchBody(changes as unknown as Partial<LegacyAssetInstance>),
    headers: scope?.headers,
  }));
  if (error) throw error;
  return assetInstanceRow(data);
}

export class AssetAdapter implements AssetService {
  constructor(
    private readonly bridge: RendererBridge,
    private readonly analytics?: IAnalyticsService
  ) { }

  // The library browse set: public uploads and Lureo's listed items (display flag on)
  async fetchAssetTypes(type?: string): Promise<any[]> {
    // No category matched no rows on the generic route
    if (type === undefined) return [];
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/asset-types/library', {
        params: { query: { category: [type], ...page } },
      }));

      if (error) {
        throw error;
      }

      return data.map(dto => convertToAsset(assetTypeRow(dto)));
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async getAssetTypeIdBySourceNameAndCategory(sourceName: string, category: string): Promise<string | null> {
    try {
      return (await firstAssetType({ sourceName, category: [category] }))?.asset_type_id || null;
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async fetchTriggerInstancesByScene(sceneInstanceId: string): Promise<SupabaseAssetInstance[]> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/asset-instances', {
        params: { query: { sceneUniqueId: [sceneInstanceId], category: ['trigger'], ...page } },
      }));
      if (error) throw error;
      return data.map(assetInstanceRow);
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  // All trigger asset types (Proximity seeded today; Gaze/Touch later). Used to
  // resolve a gate instance's asset_type_id back to the stable source_name.
  async fetchTriggerAssetTypes(): Promise<SupabaseAssetType[]> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/asset-types', {
        params: { query: { category: ['trigger'], ...page } },
      }));
      if (error) throw error;
      return data.map(assetTypeRow);
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  // The trigger asset type backs gates (asset instances of category 'trigger').
  // Proximity is the only seeded variant today; Gaze follows when seeded.
  async fetchTriggerAssetType(): Promise<SupabaseAssetType | null> {
    try {
      return await firstAssetType({ sourceName: 'Trigger-Proximity', category: ['trigger'] });
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  /**
   * Get asset type by source name and category in a single query (optimized: combines ID lookup + row fetch)
   * Use this when you need the full asset type data, not just the ID
   */
  async getAssetTypeBySourceNameAndCategory(sourceName: string, category: string): Promise<SupabaseAssetType> {
    try {
      const row = await firstAssetType({ sourceName, category: [category] });

      if (!row) {
        throw new Error(`Asset type with source name '${sourceName}' and category '${category}' not found`);
      }

      return row;
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  /**
   * Get camera asset type in a single query (optimized: combines ID lookup + row fetch)
   */
  async getCameraAssetType(): Promise<SupabaseAssetType> {
    return this.getAssetTypeBySourceNameAndCategory(ASSET_SOURCES.CAMERA, ASSET_CATEGORIES.CAMERA);
  }

  // Rows in an array, empty when there is none, as the generic route's filtered select returned them
  async fetchAssetTypeRow(assetTypeId: string): Promise<any> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/asset-types/{assetTypeUniqueId}', { params: { path: { assetTypeUniqueId: assetTypeId } } }));

      if (error?.status === 404) return [];
      if (error) {
        throw error;
      }

      return [assetTypeRow(data)];
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async fetchAssetInstanceRow(assetInstanceId: string): Promise<any> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/asset-instances/{uniqueId}', { params: { path: { uniqueId: assetInstanceId } } }));

      if (error?.status === 404) return [];
      if (error) {
        throw error;
      }

      return [assetInstanceRow(data)];
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async addAssetInstanceRow(newAssetInstance: SupabaseAssetInstance, scope?: WriteScope): Promise<SupabaseAssetInstance> {
    try {
      const placed = await this.placeAssetInstance(newAssetInstance, [], [], scope);
      sendPlacement(this.bridge, placed.saved, false);
      // An array, as callers read [0] from the generic route's insert().select()
      return [placed.assetInstance] as unknown as SupabaseAssetInstance;
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  /**
   * Places an asset with the clips and keyframes it starts with in one transaction
   * (createAssetInstance), so a placement never lands half made.
   */
  private async placeAssetInstance(
    assetInstance: SupabaseAssetInstance,
    clipInstances: SupabaseClipInstance[],
    keyframes: CreateKeyframeData[],
    scope?: WriteScope
  ): Promise<{ assetInstance: SupabaseAssetInstance; clipInstances: SupabaseClipInstance[]; keyframes: KeyframeDto[]; saved: Placement }> {
    const body = {
      ...assetInstanceCreateBody(assetInstance as unknown as Partial<LegacyAssetInstance>),
      clipInstances: clipInstances.map(clipInstanceBody),
      keyframes: keyframes.map(keyframe => keyframeCreateBody(keyframe as Partial<LegacyKeyframe>)),
    };
    const { data, error } = await toResult(v1.POST('/api/v1/asset-instances', { body, headers: scope?.headers }));
    if (error) throw error;
    return { assetInstance: assetInstanceRow(data.assetInstance), clipInstances: data.clipInstances.map(clipInstanceRow), keyframes: data.keyframes, saved: data };
  }

  async deleteAssetInstanceRow(assetId: string, onAssetDeleted?: AnchorCategoryUpdate, scope?: WriteScope): Promise<void> {
    try {
      // If we need to call the callback, fetch the scene_instance_id and category first
      let sceneInstanceId: string | null = null;
      let category: string | null = null;
      let anchorImage: string | null = null;
      if (onAssetDeleted) {
        const { data: assetData, error: fetchError } = await toResult(v1.GET('/api/v1/asset-instances/{uniqueId}', {
          params: { path: { uniqueId: assetId } },
        }));

        // One already gone leaves the renderer and is deleted; a read that failed otherwise is the delete's failure
        if (fetchError) {
          if (fetchError.status !== 404) throw fetchError;
          this.bridge.send({ type: 'assetInstanceRemoved', assetInstanceUniqueId: assetId });
          return;
        }

        sceneInstanceId = assetData.sceneUniqueId;
        category = assetData.category ?? null;
        const thumbnailed = category === ASSET_CATEGORIES.IMMERSAL || category === ASSET_CATEGORIES.GEOSPATIAL;
        anchorImage = thumbnailed && assetData.imgUrl && !isPlaceholderThumbnailUrl(assetData.imgUrl) ? assetData.imgUrl : null;
      }

      // Delete the asset with its clips and keyframes; one already gone is deleted, as the generic route's filtered delete treated it
      const { error } = await toResult(v1.DELETE('/api/v1/asset-instances/{uniqueId}', { params: { path: { uniqueId: assetId } }, headers: scope?.headers }));

      if (error && error.status !== 404) {
        throw error;
      }
      // Once the row is gone the renderer despawns it, with its keyframes and clips
      this.bridge.send({ type: 'assetInstanceRemoved', assetInstanceUniqueId: assetId });

      // Its thumbnail, still the story's scan image, is marked as an anchor's before the scan image is seeded again
      if (anchorImage && sceneInstanceId) {
        await markAnchorThumbnails(sceneInstanceId, anchorImage)
          .catch((markError: unknown) => console.warn('Failed to mark the deleted anchor\'s thumbnail:', markError));
      }

      // Update the scene's anchor category after asset deletion
      if (onAssetDeleted && sceneInstanceId) {
        try {
          await onAssetDeleted(sceneInstanceId, scope, category);
        } catch (error) {
          console.warn('Failed to update scene anchor category after asset deletion:', error);
          // Don't fail the entire operation if this update fails
        }
      }
    } catch (error) {
      captureError(error, { domain: 'asset', operation: 'deleteAssetInstanceRow', assetId });
      console.error('Error deleting row:', error);
      throw error;
    }
  }

  async deleteAssetType(assetTypeId: string, scope?: WriteScope): Promise<void> {
    try {
      const { currentScene, liveSceneSync } = await import('@/modules/undoRedo/scenePorts');
      const sceneId = currentScene();
      // Its instances and stored files go with it (a copied type still pointing into the folder keeps them)
      const { data, error } = await toResult(v1.DELETE('/api/v1/asset-types/{assetTypeUniqueId}', {
        params: { path: { assetTypeUniqueId: assetTypeId }, query: { cascade: true, removeStoredFiles: true } },
        headers: scope?.headers,
      }));
      if (error) throw error;
      // Its placements in the open scene went with it: they leave the renderer and the editor, unless another scene opened meanwhile
      await liveSceneSync(this.bridge, () => currentScene() === sceneId).hideDeletion(data);
    } catch (error) {
      captureError(error, { domain: 'asset', operation: 'deleteAssetType', assetTypeId });
      console.error('Error deleting asset type:', error);
      throw error;
    }
  }

  async updateAssetTypeDisplayName(assetTypeId: string, newDisplayName: string, scope?: WriteScope): Promise<void> {
    try {
      const { error } = await toResult(v1.PATCH('/api/v1/asset-types/{assetTypeUniqueId}', {
        params: { path: { assetTypeUniqueId: assetTypeId } },
        body: assetTypePatchBody({ asset_type_display_name: newDisplayName } as Partial<LegacyAssetType>),
        headers: scope?.headers,
      }));

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating asset type display name:', error);
      throw error;
    }
  }



  /**
   * Merge one behaviour flag into asset_instance_details. The blob is shared with
   * trigger tuning and the anchor is_horizontal flag, so the stored value is
   * re-read and spread first, a flag-only write would silently drop whatever
   * else the row was carrying.
   *
   * The read and the write are separate round trips, so this is last-write-wins,
   * not atomic: a concurrent write to the same row's details between the two can
   * be lost. Making it atomic needs a server-side jsonb merge, which the generic
   * table update cannot express. Tolerable today because every writer is a discrete
   * user action on one instance (movable on 3D placements, is_horizontal on anchors,
   * tuning on category 'trigger', uniform_scale on any keyframed instance), so a loss
   * needs two of them on the same row inside one round trip.
   */
  async setAssetInstanceDetailFlag(assetInstanceId: string, key: string, value: boolean, scope?: WriteScope): Promise<void> {
    const rows = await this.fetchAssetInstanceRow(assetInstanceId);
    const row = Array.isArray(rows) ? rows[0] : rows;
    // A double-encoded row would otherwise spread character by character and lose its other keys.
    const current = safeParseJsonObject(row?.asset_instance_details);
    if (current[key] === value) return;
    await this.updateAssetInstanceDetails(assetInstanceId, { ...current, [key]: value }, scope);
  }

  async updateAssetInstanceDisplayName(assetInstanceId: string, displayName: string, scope?: WriteScope): Promise<void> {
    try {
      await patchAssetInstance(assetInstanceId, { asset_instance_display_name: displayName }, scope);
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async updateAssetInstanceDetails(assetInstanceId: string, details: Record<string, unknown>, scope?: WriteScope): Promise<void> {
    try {
      const row = await patchAssetInstance(assetInstanceId, { asset_instance_details: details }, scope);

      // Mirror updateAssetInstanceRowPosition: notify the renderer so a live gate re-reads its detection
      // params (radius / maxAngle / dwellSeconds / delay) without a scene reload. Without this the
      // edit only reached the renderer on the next scene load (Triggers v2 review Issue 3a).
      this.sendAssetInstanceUpdated(row);
    } catch (error) {
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async updateAssetInstanceRowPosition(assetInstanceId: string, beginTime: number, endTime: number, scope?: WriteScope): Promise<void> {
    try {
      const row = await patchAssetInstance(assetInstanceId, { asset_instance_begin_time: beginTime, asset_instance_end_time: endTime }, scope);

      // Send the renderer the full updated row
      this.sendAssetInstanceUpdated(row);
    } catch (error) {
      console.error(`Error updating asset_instance_row for ${assetInstanceId}:`, error);
      throw error;
    }
  }

  /** Saves only where the asset ends, so a move of its start saved meanwhile stays. */
  async updateAssetInstanceEndTime(assetInstanceId: string, endTime: number, scope?: WriteScope): Promise<void> {
    const row = await patchAssetInstance(assetInstanceId, { asset_instance_end_time: endTime }, scope);
    this.sendAssetInstanceUpdated(row);
  }

  async updateAssetInstanceLockState(assetInstanceId: string, isLocked: boolean, scope?: WriteScope): Promise<any> {
    try {
      const row = await patchAssetInstance(assetInstanceId, { asset_instance_is_locked: isLocked }, scope);
      this.bridge.send({ type: 'assetInstanceLockChanged', assetInstance: assetInstanceFromLegacy(row as unknown as LegacyAssetInstance) });
      return [row];
    } catch (error) {
      console.error('🔴 ERROR in updateAssetInstanceLockState:', error);
      console.error('Details:', error);
      throw error;
    }
  }

  async updateAssetInstanceActiveState(instanceId: string, isActive: boolean, scope?: WriteScope): Promise<void> {
    const row = await patchAssetInstance(instanceId, { asset_instance_is_active: isActive }, scope);
    this.bridge.send({ type: 'assetInstanceVisibilityChanged', assetInstance: assetInstanceFromLegacy(row as unknown as LegacyAssetInstance) });
  }

  async batchUpdateAssetInstanceDisplayOrders(updates: Array<{ assetInstanceId: string; displayOrder: number | null }>, scope?: WriteScope): Promise<void> {
    try {
      // One all-or-nothing batch per 500 instances (updateAssetInstances); an instance deleted meanwhile is skipped
      for (let start = 0; start < updates.length; start += PAGE_LIMIT) {
        const items = updates.slice(start, start + PAGE_LIMIT).map(({ assetInstanceId, displayOrder }) =>
          assetInstanceBatchItem(assetInstanceId, { asset_instance_display_order: displayOrder })
        );
        const error = await sendBatchSkippingMissing(items, batch => v1.POST('/api/v1/asset-instances/batch', { body: { items: batch }, headers: scope?.headers }));
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error batch updating asset_instance_display_orders:', error);
      throw error;
    }
  }

  async fetchAllSceneAssets(sceneId: string): Promise<any[]> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/asset-instances', {
        params: { query: { sceneUniqueId: [sceneId], ...page } },
      }));

      if (error) {
        throw error;
      }

      return data.map(assetInstanceRow);
    } catch (error) {
      console.error('Error fetching scene assets:', error);
      throw error;
    }
  }

  async addAssetToScene(
    asset: { typeId: string; type?: string; typeDisplayName: string; assetTypeId?: string },
    userId: string,
    sceneInstanceId: string,
    environmentPreferences?: { keyframes_on?: boolean },
    onSceneAnchorCategoryUpdate?: AnchorCategoryUpdate,
    assetTypeData?: SupabaseAssetType, // Optional: if provided, skip the fetch
    instanceOverrides?: Partial<SupabaseAssetInstance>,
    mediaClipOverrides?: MediaClipOverrides,
    scope?: WriteScope
  ): Promise<SupabaseAssetInstance> {
    try {
      // Get asset type ID (for audio/image/video, typeId may be asset_instance_id - use assetTypeId when provided)
      const assetTypeId = asset.assetTypeId ?? (await this.getAssetTypeId(asset));

      // Create asset instance (pass assetTypeData if provided to avoid extra query)
      const newAssetInstance = await this.createAssetInstance(
        asset,
        userId,
        sceneInstanceId,
        assetTypeId,
        assetTypeData,
        instanceOverrides
      );

      // The playback clip (audio, video) and the start keyframe (when keyframes are on, not for
      // audio) are created with the instance in one transaction
      const mediaClip = await this.mediaClipFor(asset, newAssetInstance, mediaClipOverrides);
      const startKeyframe = environmentPreferences?.keyframes_on === true && newAssetInstance.asset_instance_category !== 'audio'
        ? this.startKeyframeFor(newAssetInstance)
        : null;
      // The start keyframe carries the pose; the row's pose columns, which nothing reads, are not written
      const placed = await this.placeAssetInstance(
        { ...newAssetInstance, asset_instance_position: undefined, asset_instance_rotation: undefined, asset_instance_scale: undefined } as unknown as SupabaseAssetInstance,
        mediaClip ? [mediaClip] : [],
        startKeyframe ? [startKeyframe] : [],
        scope
      );
      const newAssetInstanceData = placed.assetInstance;

      // A position the caller gives (a split, a duplicate, the text and form editors) stands over where the renderer places the start keyframe
      const savedStart = placed.keyframes.find((keyframe) => keyframe.category === 'start');
      if (savedStart && instanceOverrides?.asset_instance_position) poseStated(this.editorKeyframe(savedStart));

      // The asset instance first, so the renderer registers it, then its keyframes and its playback clip
      sendPlacement(this.bridge, placed.saved, false);

      // Update the scene's anchor category based on the new asset
      if (onSceneAnchorCategoryUpdate) {
        try {
          await onSceneAnchorCategoryUpdate(sceneInstanceId, scope, newAssetInstanceData.asset_instance_category);
        } catch (error) {
          console.warn('Failed to update scene anchor category:', error);
          // Don't fail the entire operation if this update fails
        }
      }

      this.analytics?.trackLureoAssetAdded({
        scene_instance_id: sceneInstanceId,
        asset_type_id: assetTypeId,
        asset_type_source_name: newAssetInstanceData.asset_instance_source_name,
        asset_category: newAssetInstanceData.asset_instance_category ?? undefined,
        asset_name: asset.typeDisplayName,
      });

      if (newAssetInstanceData.asset_instance_category === ASSET_CATEGORIES.IMAGE_ANCHOR) {
        this.analytics?.trackLureoImageAnchorAdded({
          scene_instance_id: sceneInstanceId,
          asset_instance_id: newAssetInstanceData.asset_instance_id,
          asset_instance_source_name: newAssetInstanceData.asset_instance_source_name,
          asset_category: newAssetInstanceData.asset_instance_category ?? undefined,
        });
      }

      return newAssetInstanceData;
    } catch (error) {
      captureError(error, { domain: 'asset', operation: 'addAssetToScene', assetTypeId: asset.typeId, sceneInstanceId });
      console.error('Error adding asset to scene:', error);
      throw error;
    }
  }

  private async getAssetTypeId(asset: { typeId: string; type?: string }): Promise<string> {
    if (asset.type === 'audio' || asset.type === 'image' || asset.type === 'video') {
      // For audio/image/video assets, the typeId could be either:
      // 1. asset_instance_id (from "Assets in Scene", "Project Assets", or "My" sections) - need to look up asset_type_id
      // 2. asset_type_id (from "Available" sections or "My Videos") - can use directly

      try {
        // First, try to fetch as asset_instance_id (for scene/project sections)
        const assetInstance = await this.fetchAssetInstanceRow(asset.typeId);
        if (assetInstance && assetInstance[0]) {
          return assetInstance[0].asset_type_id;
        }
      } catch {
        // If that fails, assume it's already an asset_type_id
      }

      // If we get here, the typeId is already an asset_type_id
      return asset.typeId;
    }
    // For 3D assets and scan assets, typeId is always asset_type_id
    return asset.typeId;
  }

  private async createAssetInstance(
    asset: { typeId: string; type?: string; typeDisplayName: string },
    userId: string,
    sceneInstanceId: string,
    assetTypeId: string,
    assetTypeData?: SupabaseAssetType, // Optional: if provided, skip the fetch
    instanceOverrides?: Partial<SupabaseAssetInstance>
  ): Promise<SupabaseAssetInstance> {
    let baseInstance: SupabaseAssetInstance;

    // Extract begin_time from overrides to pass to createAssetInstance so end_time is calculated correctly
    const beginTime = instanceOverrides?.asset_instance_begin_time;

    if (asset.type === 'audio' || asset.type === 'image' || asset.type === 'video') {
      baseInstance = await this.createAudioImageAssetInstance(asset, userId, sceneInstanceId, assetTypeId, beginTime, assetTypeData);
    } else {
      // Use provided asset type data if available, otherwise fetch it
      const typeData = this.hasAssetInstanceRequiredFields(assetTypeData)
        ? assetTypeData
        : (await this.fetchAssetTypeRow(assetTypeId))[0];
      // Pass begin_time to createAssetInstance so end_time is calculated correctly
      baseInstance = createAssetInstance(userId, sceneInstanceId, typeData, beginTime !== undefined ? { begin_time: beginTime } : undefined);
    }

    if (!instanceOverrides) {
      return baseInstance;
    }

    // Apply safe overrides while preserving identity fields
    // Note: end_time may need recalculation if begin_time was overridden
    const finalInstance = {
      ...baseInstance,
      ...instanceOverrides,
      asset_instance_id: baseInstance.asset_instance_id,
      user_id: baseInstance.user_id,
      scene_instance_id: baseInstance.scene_instance_id,
      asset_type_id: baseInstance.asset_type_id
    };

    // If begin_time was overridden, recalculate end_time based on duration
    if (instanceOverrides.asset_instance_begin_time !== undefined && instanceOverrides.asset_instance_end_time === undefined) {
      const duration = baseInstance.asset_instance_end_time - baseInstance.asset_instance_begin_time;
      finalInstance.asset_instance_end_time = instanceOverrides.asset_instance_begin_time + duration;
    }

    return finalInstance;
  }

  private async createAudioImageAssetInstance(
    asset: { typeId: string; type?: string },
    userId: string,
    sceneInstanceId: string,
    assetTypeId: string,
    beginTime?: number,
    providedAssetTypeData?: SupabaseAssetType
  ): Promise<SupabaseAssetInstance> {
    const assetTypeData = this.hasAssetInstanceRequiredFields(providedAssetTypeData)
      ? providedAssetTypeData
      : (await this.fetchAssetTypeRow(assetTypeId))[0];
    if (!assetTypeData) {
      throw new Error(`Asset type not found for id: ${assetTypeId}`);
    }

    // Determine duration based on asset type and category
    let duration = 6; // default for regular assets
    if (asset.type === 'audio' || asset.type === 'video') {
      const span = assetTypeData.asset_type_end_time - assetTypeData.asset_type_begin_time;
      duration = Number.isFinite(span) && span > 0 ? span : 6;
    } else if (isBackgroundAsset(assetTypeData.asset_type_category)) {
      duration = INITIAL_TIMELINE_LENGTH_SECONDS;
    } else if (assetTypeData.asset_type_category === 'character') {
      duration = 10; // 10 seconds for characters
    }

    const startTime = beginTime ?? 0;

    // Create the asset instance using asset type data
    const assetInstanceData: SupabaseAssetInstance = {
      user_id: userId,
      scene_instance_id: sceneInstanceId,
      asset_instance_id: Uid(),
      asset_type_id: assetTypeId,
      asset_instance_category: asset.type || null,
      asset_instance_source_name: assetTypeData.asset_type_source_name || '',
      asset_instance_display_name: assetTypeData.asset_type_display_name || '',
      asset_instance_img_url: assetTypeData.asset_type_img_url || '',
      asset_instance_details: assetTypeData.asset_type_details || null,
      asset_instance_source_url: assetTypeData.asset_type_source_url || '',
      asset_instance_position: '0.00, 0.00, 0.00',
      asset_instance_rotation: '0.00, 0.00, 0.00',
      asset_instance_scale: '1.00, 1.00, 1.00',
      asset_instance_begin_time: startTime,
      asset_instance_end_time: startTime + duration,
      asset_instance_is_active: true,
      asset_instance_is_locked: false,
      asset_instance_is_billboarded: false,
      asset_instance_has_packed_alpha: assetTypeData.asset_type_has_packed_alpha ?? false
    };

    return assetInstanceData;
  }

  private hasAssetInstanceRequiredFields(assetTypeData?: SupabaseAssetType): assetTypeData is SupabaseAssetType {
    return Boolean(
      assetTypeData &&
        assetTypeData.asset_type_source_name !== undefined &&
        assetTypeData.asset_type_source_url !== undefined
    );
  }

  /** The playback clip an audio or video placement starts with; none for other assets. */
  private async mediaClipFor(asset: { type?: string }, assetInstanceData: SupabaseAssetInstance, media?: MediaClipOverrides): Promise<SupabaseClipInstance | null> {
    if (asset.type !== 'audio' && asset.type !== 'video') return null;
    return mediaClipInstanceRow(
      asset.type,
      await mediaClipTypeId(asset.type),
      assetInstanceData.asset_instance_id,
      assetInstanceData.scene_instance_id,
      assetInstanceData.asset_instance_end_time - assetInstanceData.asset_instance_begin_time,
      assetInstanceData.asset_instance_source_name,
      assetInstanceData.asset_instance_begin_time,
      media
    );
  }

  private sendAssetInstanceUpdated(row: SupabaseAssetInstance): void {
    this.bridge.send({ type: 'assetInstanceUpdated', assetInstance: assetInstanceFromLegacy(row as unknown as LegacyAssetInstance) });
  }

  /** The start keyframe at the instance's own pose and begin time, as KeyframeServiceImpl.createDefaultKeyframesForAsset makes it. */
  private startKeyframeFor(assetInstanceData: SupabaseAssetInstance): CreateKeyframeData {
    // A pose carried over (a swap, an override) never reaches an anchor's keyframe
    const pose = placedKeyframeTransform(assetInstanceData.asset_instance_category, {
      position: this.parseVectorString(assetInstanceData.asset_instance_position),
      rotation: this.parseVectorString(assetInstanceData.asset_instance_rotation),
      scale: this.parseVectorString(assetInstanceData.asset_instance_scale, { x: 1, y: 1, z: 1 }),
    });
    return {
      keyframe_id: crypto.randomUUID(),
      asset_instance_id: assetInstanceData.asset_instance_id,
      keyframe_time: assetInstanceData.asset_instance_begin_time,
      keyframe_position: formatVector3String(pose.position, 3),
      keyframe_rotation: formatVector3String(pose.rotation, 3),
      keyframe_scale: formatVector3String(pose.scale, 3),
      keyframe_category: 'start',
      scene_instance_id: assetInstanceData.scene_instance_id
    };
  }

  /** A saved keyframe as the editor holds it. */
  private editorKeyframe(dto: KeyframeDto): Keyframe {
    return {
      id: dto.uniqueId,
      assetInstanceId: dto.assetInstanceUniqueId,
      sceneInstanceId: dto.sceneUniqueId,
      timePosition: dto.time,
      position: parseVector3String(dto.position) ?? undefined,
      rotation: parseVector3String(dto.rotation) ?? undefined,
      scale: parseVector3String(dto.scale) ?? undefined,
      category: dto.category ?? undefined,
      createdAt: dto.createdAt,
    };
  }

  // The start keyframe, saved and sent to the renderer
  private async createDefaultKeyframesForAsset(assetInstanceData: SupabaseAssetInstance, scope?: WriteScope): Promise<void> {
    try {
      // Import the keyframe adapter dynamically to avoid circular dependencies
      const { KeyframeAdapter } = await import('@/infrastructure/adapters/KeyframeAdapter');
      const [row] = await new KeyframeAdapter().addKeyframeRow(this.startKeyframeFor(assetInstanceData), scope);
      new KeyframeBridgeAdapter(this.bridge).keyframeAdded(row);
    } catch (error) {
      console.error('Error creating default keyframes:', error);
      throw error;
    }
  }

  private parseVectorString(vectorString: string, fallback = { x: 0, y: 0, z: 0 }): { x: number; y: number; z: number } {
    const parsed = parseVector3String(vectorString);
    if (!parsed) {
      console.warn('Failed to parse vector string, using defaults:', vectorString);
      return fallback;
    }
    return parsed;
  }

}
